import { useState, useMemo } from 'react';
import type { ProjectBudget, TimeEntry, Profile } from '../types';
import {
  calculateBudgetConsumption,
  calculateUserNominalProgress,
  type BudgetThresholdStatus
} from '../utils/timeCalculator';
import {
  Calendar,
  Clock,
  Users,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Edit2,
  ChevronDown,
  ChevronUp,
  Briefcase
} from 'lucide-react';

interface SharedBudgetCardProps {
  budget: ProjectBudget;
  projectName?: string;
  entries: TimeEntry[];
  currentUserId?: string;
  isSupervisor?: boolean;
  profiles?: Profile[];
  onEdit?: (budget: ProjectBudget) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function SharedBudgetCard({
  budget,
  projectName = '',
  entries,
  currentUserId,
  isSupervisor = false,
  profiles = [],
  onEdit,
  isCollapsed,
  onToggleCollapse
}: SharedBudgetCardProps) {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [showMembers, setShowMembers] = useState(false);

  const collapsed = isCollapsed !== undefined ? isCollapsed : internalCollapsed;

  const toggleCollapse = () => {
    if (onToggleCollapse) {
      onToggleCollapse();
    } else {
      setInternalCollapsed(prev => !prev);
    }
  };

  // Calculate budget stats
  const stats = useMemo(() => {
    return calculateBudgetConsumption(entries, budget);
  }, [entries, budget]);

  // Calculate current user's personal progress against their nominal share
  const userProgress = useMemo(() => {
    const userDays = currentUserId ? (stats.memberDays[currentUserId] || 0) : 0;
    return calculateUserNominalProgress(
      userDays,
      stats.nominalShareDays,
      budget.warningThresholdPercent ?? 80
    );
  }, [currentUserId, stats, budget.warningThresholdPercent]);

  // Profile lookup
  const profileMap = useMemo(() => {
    const map = new Map<string, Profile>();
    profiles.forEach(p => map.set(p.id, p));
    return map;
  }, [profiles]);

  const getStatusBadge = (status: BudgetThresholdStatus, labelPrefix?: string) => {
    switch (status) {
      case 'critical':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-50 text-red-700 border border-red-200">
            <AlertCircle className="w-3.5 h-3.5 text-red-500" />
            {labelPrefix ? `${labelPrefix}: ` : ''}Over Budget
          </span>
        );
      case 'warning':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            {labelPrefix ? `${labelPrefix}: ` : ''}Approaching Limit
          </span>
        );
      case 'normal':
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
            {labelPrefix ? `${labelPrefix}: ` : ''}On Track
          </span>
        );
    }
  };

  const getProgressColor = (status: BudgetThresholdStatus) => {
    switch (status) {
      case 'critical':
        return 'bg-red-500';
      case 'warning':
        return 'bg-amber-500';
      case 'normal':
      default:
        return 'bg-emerald-500';
    }
  };

  const memberCount = budget.members?.length ?? 0;
  const totalPoolDays = budget.contingentDays ?? budget.budgetDays ?? 0;
  const isGlobalForUser = !isSupervisor && Boolean(budget.isGlobal);

  return (
    <div className={`bg-white rounded-xl border border-slate-200/80 shadow-xs hover:border-slate-300 hover:shadow-sm transition-all overflow-hidden ${collapsed ? '' : 'ring-1 ring-slate-200/60'}`}>
      {/* Card Header / Summary Row */}
      <div
        onClick={toggleCollapse}
        className="p-3.5 sm:px-5 sm:py-3 cursor-pointer select-none bg-gradient-to-r from-slate-50/70 via-white to-slate-50/40 hover:bg-slate-50/90 transition-colors flex flex-wrap items-center justify-between gap-3"
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleCollapse();
            }}
            className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition shrink-0"
            aria-label={collapsed ? "Expand position details" : "Collapse position details"}
          >
            <ChevronDown
              className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${
                collapsed ? '-rotate-90' : 'rotate-0'
              }`}
            />
          </button>

          <Briefcase className="w-4 h-4 text-indigo-600 shrink-0" />

          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 min-w-0">
            <h3 className="text-sm font-bold text-slate-900 truncate" title={projectName || budget.name}>
              {projectName || budget.name}
            </h3>

            {budget.contractId && (
              <span className="px-2 py-0.5 rounded-md text-[11px] font-mono font-bold bg-indigo-50 text-indigo-700 border border-indigo-100/80" title="Customer Order ID">
                {budget.contractId}
              </span>
            )}

            {budget.customerId && (
              <span className="px-2 py-0.5 rounded-md text-[11px] font-mono font-semibold bg-slate-100 text-slate-700 border border-slate-200" title="Customer Name">
                {budget.customerId}
              </span>
            )}

            {budget.customerDepartmentId && (
              <span className="px-2 py-0.5 rounded-md text-[11px] font-mono font-semibold bg-sky-50 text-sky-700 border border-sky-200/80" title="Customer Department ID">
                {budget.customerDepartmentId}
              </span>
            )}

            {budget.contract && (
              <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-100/80" title="SPF Contract ID">
                {budget.contract}
              </span>
            )}

            {budget.serviceType && (
              <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-amber-50 text-amber-800 border border-amber-200/80" title="Service Type">
                {budget.serviceType}
              </span>
            )}

            {budget.name && projectName && budget.name !== projectName && (
              <span className="text-xs font-medium text-slate-500 hidden md:inline truncate">
                • {budget.name}
              </span>
            )}

            {budget.isGlobal ? (
              <span className="px-2 py-0.5 rounded-md text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">
                Global
              </span>
            ) : collapsed && (
              <span className="text-[11px] text-slate-500 font-medium hidden sm:inline-flex items-center gap-1">
                <Users className="w-3.5 h-3.5 text-slate-400" />
                {`${memberCount} ${memberCount === 1 ? 'member' : 'members'}`}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2.5 sm:gap-3 shrink-0">
          {collapsed && (
            <>
              <div className="text-right" title={isGlobalForUser ? "Your booked days" : undefined}>
                <span className="text-sm font-bold text-slate-800">
                  {isGlobalForUser ? `${userProgress.userDays.toFixed(1)}d` : `${stats.totalDays.toFixed(1)}d`}
                </span>
                {!isGlobalForUser && totalPoolDays > 0 && (
                  <>
                    <span className="text-xs text-slate-400 font-normal"> / {totalPoolDays.toFixed(1)}d</span>
                    <span className="text-xs text-slate-500 ml-1.5 font-medium hidden md:inline">
                      ({stats.percentConsumed.toFixed(1)}%)
                    </span>
                  </>
                )}
              </div>

              {!isGlobalForUser && totalPoolDays > 0 && (
                <div className="w-16 sm:w-24 h-2 bg-slate-100 rounded-full overflow-hidden hidden sm:block">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${getProgressColor(
                      stats.teamThresholdStatus
                    )}`}
                    style={{ width: `${Math.min(100, stats.percentConsumed)}%` }}
                  />
                </div>
              )}
            </>
          )}

          {!isGlobalForUser && totalPoolDays > 0 && getStatusBadge(stats.teamThresholdStatus, 'Team Total')}

          {isSupervisor && onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(budget);
              }}
              className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition cursor-pointer"
              title="Edit Position"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Card Content (Collapsible) */}
      {!collapsed && (
        <div className="p-4 sm:p-5 border-t border-slate-100 space-y-4 bg-white">
          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 font-medium">
            {budget.startDate && budget.endDate && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                {budget.startDate} to {budget.endDate}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5 text-slate-400" />
              {budget.isGlobal ? 'All team members' : `${memberCount} assigned ${memberCount === 1 ? 'member' : 'members'}`}
            </span>
            {budget.customerId && (
              <span className="flex items-center gap-1">
                <span className="text-slate-400">Customer Name:</span> {budget.customerId}
              </span>
            )}
            {budget.customerDepartmentId && (
              <span className="flex items-center gap-1">
                <span className="text-slate-400">Customer Dept ID:</span> {budget.customerDepartmentId}
              </span>
            )}
            {budget.contract && (
              <span className="flex items-center gap-1">
                <span className="text-slate-400">SPF Contract ID:</span> {budget.contract}
              </span>
            )}
            {budget.serviceType && (
              <span className="flex items-center gap-1">
                <span className="text-slate-400">Service:</span> {budget.serviceType}
              </span>
            )}
            {totalPoolDays > 0 && !isGlobalForUser && (
              <span className="text-slate-400">
                Warning at {budget.warningThresholdPercent ?? 80}%
              </span>
            )}
          </div>

          {/* Team Total Full Progress Bar (hidden for regular users on global positions) */}
          {!isGlobalForUser && (
            <div className="space-y-1.5">
              <div className="flex justify-between items-baseline text-xs">
                <span className="font-semibold text-slate-700 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-indigo-500" />
                  {totalPoolDays > 0 ? 'Team Total Pool' : 'Total Booked'}
                </span>
                <div className="text-right">
                  <span className="font-bold text-slate-800">
                    {stats.totalDays.toFixed(1)}d
                  </span>
                  {totalPoolDays > 0 && (
                    <>
                      <span className="text-slate-400"> / {totalPoolDays.toFixed(1)}d</span>
                      <span className="text-xs text-slate-500 ml-1.5 font-medium">
                        ({stats.percentConsumed.toFixed(1)}%)
                      </span>
                    </>
                  )}
                </div>
              </div>

              {totalPoolDays > 0 && (
                <>
                  <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden p-0.5">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${getProgressColor(
                        stats.teamThresholdStatus
                      )}`}
                      style={{ width: `${Math.min(100, stats.percentConsumed)}%` }}
                    />
                  </div>

                  <div className="flex justify-between text-xs text-slate-500 font-medium">
                    <span>{stats.remainingDays.toFixed(1)} days remaining in shared pool</span>
                    {stats.totalDays > totalPoolDays && (
                      <span className="text-red-600 font-semibold">
                        +{(stats.totalDays - totalPoolDays).toFixed(1)}d over contingent
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* User's Personal Share Card (hidden in supervisor view) */}
          {!isSupervisor && currentUserId && (
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80 space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-indigo-500" />
                {totalPoolDays > 0 && !isGlobalForUser ? 'Your Contribution & Nominal Share' : 'Your Contribution'}
              </span>
              {totalPoolDays > 0 && !isGlobalForUser && getStatusBadge(userProgress.status, 'Your Share')}
            </div>

            <div className={`grid gap-3 pt-1 ${totalPoolDays > 0 && !isGlobalForUser ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-1'}`}>
              <div>
                <div className="text-xs text-slate-500 font-medium">Booked by You</div>
                <div className="text-base font-bold text-slate-800">
                  {userProgress.userDays.toFixed(1)}d
                </div>
              </div>
              {totalPoolDays > 0 && !isGlobalForUser && (
                <>
                  <div>
                    <div className="text-xs text-slate-500 font-medium">Nominal Share</div>
                    <div className="text-base font-bold text-slate-800">
                      {userProgress.nominalShareDays.toFixed(1)}d
                    </div>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <div className="text-xs text-slate-500 font-medium">Share Consumed</div>
                    <div className="text-base font-bold text-slate-800">
                      {userProgress.percentOfShare.toFixed(1)}%
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Personal Progress Bar */}
            {totalPoolDays > 0 && !isGlobalForUser && (
              <div className="h-2 w-full bg-slate-200/80 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${getProgressColor(
                    userProgress.status
                  )}`}
                  style={{ width: `${Math.min(100, userProgress.percentOfShare)}%` }}
                />
              </div>
            )}
          </div>
        )}

        {/* Member Breakdown (Supervisors or Drilldown) */}
        {isSupervisor && budget.members && budget.members.length > 0 && (
          <div className="pt-2 border-t border-slate-100">
            <button
              onClick={() => setShowMembers(prev => !prev)}
              className="flex items-center justify-between w-full text-xs font-bold text-slate-600 uppercase tracking-wider hover:text-indigo-600 transition py-1"
            >
              <span>Team Members Breakdown ({budget.members.length})</span>
              {showMembers ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {showMembers && (
              <div className="mt-3 divide-y divide-slate-100 border border-slate-200/80 rounded-xl overflow-hidden bg-slate-50/50">
                {budget.members.map(memberId => {
                  const profile = profileMap.get(memberId);
                  const memberDays = stats.memberDays[memberId] || 0;
                  const progress = calculateUserNominalProgress(
                    memberDays,
                    stats.nominalShareDays,
                    budget.warningThresholdPercent ?? 80
                  );

                  return (
                    <div
                      key={memberId}
                      className="p-3 sm:px-4 flex flex-wrap items-center justify-between gap-2 text-sm bg-white hover:bg-slate-50 transition"
                    >
                      <div className="min-w-[180px]">
                        <div className="font-medium text-slate-800 truncate">
                          {profile?.email || memberId}
                        </div>
                        <div className="text-xs text-slate-400">
                          {totalPoolDays > 0
                            ? `${progress.userDays.toFixed(1)}d of ${progress.nominalShareDays.toFixed(1)}d nominal share (${progress.percentOfShare.toFixed(1)}%)`
                            : `${memberDays.toFixed(1)}d booked`}
                        </div>
                      </div>

                      {totalPoolDays > 0 && (
                        <div className="flex items-center gap-3">
                          <div className="w-24 hidden sm:block">
                            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${getProgressColor(progress.status)}`}
                                style={{ width: `${Math.min(100, progress.percentOfShare)}%` }}
                              />
                            </div>
                          </div>
                          {getStatusBadge(progress.status)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    )}
  </div>
);
}

export const PositionCard = SharedBudgetCard;
